import { memo } from "react";
import {
  Box,
  Button,
  Container,
  Grid2,
  Typography,
} from "@mui/material";
import { FavoriteBorder } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PropertyCard from "../../Components/PropertyCard/PropertyCard";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import { useGetWishlistWithDetailsQuery } from "../../Services/api.user.slice";
import LoaderOverlay from "../../Components/Loader/Loader";
import { RootState } from "../../Store/store";

const WishlistPage = memo(() => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state?.auth);

  // Fetch wishlist with details, skip if not authenticated
  const {
    data: wishlistResponse,
    isLoading,
    error,
  } = useGetWishlistWithDetailsQuery("", { skip: !isAuthenticated });

  const wishlistProperties = wishlistResponse?.data || [];

  // Not logged in state
  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl">
        <FullPaddingWrapper>
          <Box pt="25px" textAlign="center" py={8}>
            <FavoriteBorder
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Please log in to view your wishlist
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create an account or sign in to save your favorite properties.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </Box>
        </FullPaddingWrapper>
      </Container>
    );
  }

  // Loading state
  if (isLoading) {
    return <LoaderOverlay />;
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl">
        <FullPaddingWrapper>
          <Box pt="25px" textAlign="center" py={8}>
            <Typography variant="h6" color="error">
              Failed to load wishlist. Please try again.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.reload()}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Box>
        </FullPaddingWrapper>
      </Container>
    );
  }

  // Empty state
  if (wishlistProperties.length === 0) {
    return (
      <Container maxWidth="xl">
        <FullPaddingWrapper>
          <Box pt="25px" textAlign="center" py={8}>
            <FavoriteBorder
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Your wishlist is empty
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Start exploring properties and save your favorites here.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate("/")}>
              Explore Properties
            </Button>
          </Box>
        </FullPaddingWrapper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <FullPaddingWrapper>
        <Box pt="25px">
          {/* Page Header */}
          <Box mb={4}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              My Wishlist
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {wishlistProperties.length} saved{" "}
              {wishlistProperties.length === 1 ? "property" : "properties"}
            </Typography>
          </Box>

          {/* Property Grid */}
          <Grid2 container spacing={2}>
            {wishlistProperties.map((property: any, index: number) => (
              <Grid2
                key={property._id || index}
                size={{ xl: 4, lg: 4, md: 6, sm: 6, xs: 12 }}
              >
                <PropertyCard
                  id={property.propertyRefID || property._id}
                  propertyId={property.propertyId}
                  type="product"
                  isLoading={false}
                  isClaimed={property?.isClaimed}
                  featuredPhoto={property?.featuredPhoto}
                  mls={property?.mls}
                  propertyName={property.address?.address}
                  bathCount={property.propertyOverView?.bathrooms || "0"}
                  bedCount={property.propertyOverView?.bedrooms}
                  stateName={property.address?.state}
                  stateZip={property.address?.zip}
                  sqrFeet={property.propertyOverView?.lotSize}
                  href={`/${property.propertyId}/property-id/${property.propertyId}`}
                  city={property.address?.city}
                />
              </Grid2>
            ))}
          </Grid2>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default WishlistPage;
